import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { DollarSign, TrendingUp, Package, Truck } from 'lucide-react';

/**
 * Admin Overview Tab — purely presentational.
 * All state (bookings, expenses, analytics) is owned by the parent and passed in as props.
 */
const AdminOverviewTab = ({
  totalRevenue,
  netProfit,
  totalExpenses,
  bookings,
  revenueAnalytics,
  customerAnalytics,
}) => {
  return (
    <div data-testid="admin-overview-tab">
      {/* Stats Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem',
        }}
      >
        <Card style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <CardContent style={{ padding: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <p style={{ fontSize: '0.875rem', color: '#15803d', fontWeight: '600' }}>Total Revenue</p>
                <p data-testid="stat-total-revenue" style={{ fontSize: '2rem', fontWeight: 'bold', color: '#166534' }}>
                  ${totalRevenue.toFixed(2)}
                </p>
              </div>
              <DollarSign size={40} color="#22c55e" />
            </div>
          </CardContent>
        </Card>

        <Card style={{ backgroundColor: '#fef3c7', border: '1px solid #fde68a' }}>
          <CardContent style={{ padding: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <p style={{ fontSize: '0.875rem', color: '#92400e', fontWeight: '600' }}>Net Profit</p>
                <p data-testid="stat-net-profit" style={{ fontSize: '2rem', fontWeight: 'bold', color: '#78350f' }}>
                  ${netProfit.toFixed(2)}
                </p>
                <p style={{ fontSize: '0.75rem', color: '#92400e' }}>After fees &amp; expenses</p>
              </div>
              <TrendingUp size={40} color="#f59e0b" />
            </div>
          </CardContent>
        </Card>

        <Card style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
          <CardContent style={{ padding: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <p style={{ fontSize: '0.875rem', color: '#1e40af', fontWeight: '600' }}>Total Bookings</p>
                <p data-testid="stat-total-bookings" style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e3a8a' }}>
                  {bookings.length}
                </p>
              </div>
              <Package size={40} color="#3b82f6" />
            </div>
          </CardContent>
        </Card>

        <Card style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
          <CardContent style={{ padding: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <p style={{ fontSize: '0.875rem', color: '#991b1b', fontWeight: '600' }}>Total Expenses</p>
                <p data-testid="stat-total-expenses" style={{ fontSize: '2rem', fontWeight: 'bold', color: '#7f1d1d' }}>
                  ${totalExpenses.toFixed(2)}
                </p>
                <p style={{ fontSize: '0.75rem', color: '#991b1b' }}>Gas, tolls, etc.</p>
              </div>
              <Truck size={40} color="#ef4444" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Phase 1: Enhanced Analytics Cards */}
      {(revenueAnalytics || customerAnalytics) && (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1rem' }}>Business Analytics</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
            {revenueAnalytics && (
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontSize: '1rem' }}>Revenue Breakdown (30 Days)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Subscription Revenue</span>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#10b981' }}>
                        ${revenueAnalytics.subscription_revenue?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>One-Time Revenue</span>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#3b82f6' }}>
                        ${revenueAnalytics.one_time_revenue?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>Average Booking Value</span>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#f59e0b' }}>
                        ${revenueAnalytics.average_booking_value?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {customerAnalytics && (
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontSize: '1rem' }}>Customer Analytics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Customers</span>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>
                        {customerAnalytics.total_customers || 0}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Active Subscribers</span>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#10b981' }}>
                        {customerAnalytics.active_subscribers || 0}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>New This Month</span>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#3b82f6' }}>
                        {customerAnalytics.new_customers_this_month || 0}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>Subscriber Rate</span>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#f59e0b' }}>
                        {customerAnalytics.subscriber_rate?.toFixed(1) || 0}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {bookings && bookings.length > 0 ? (
              bookings.slice(0, 5).map((booking) => (
                <div
                  key={booking.id}
                  style={{
                    padding: '1rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <p style={{ fontWeight: '600' }}>#{booking.confirmation_number}</p>
                    <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>{booking.customer_email}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontWeight: '600', color: '#10b981' }}>${booking.total_cost?.toFixed(2)}</p>
                    <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>{booking.pickup_date}</p>
                  </div>
                </div>
              ))
            ) : (
              <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>No recent bookings</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminOverviewTab;
